<template>
  <header class="navbar" :class="{ 'scrolled': isScrolled }">
    <div class="navbar-inner">
      <nav class="nav-content">
        <!-- Logo on the Left -->
        <router-link to="/" class="logo">
          <img :src="'/media/recraft/logo/denvstack-icon.svg'" alt="DenvStack" class="logo-icon" >
          <span>DenvStack</span>
        </router-link>

        <!-- Persona door switcher ("Viewing as: Developer"). Desktop only —
             the drawer carries its own flat variant below 820px. -->
        <PersonaSwitcher class="nav-persona" />

        <!-- Navigation Links — single source of truth: primaryNavLinks -->
        <div class="nav-links">
          <template v-for="link in primaryNavLinks" :key="link.to || link.href">
            <!-- External links (e.g. Docs → platform-dashboard portal) use a
                 plain anchor so a full page load reaches the ingress, not the
                 SPA router. Same .nav-link styling as the router-link items. -->
            <a
              v-if="link.external"
              :href="link.href"
              class="nav-link"
              v-html="link.label"
            />
            <router-link
              v-else
              :to="link.to"
              class="nav-link"
              v-html="link.label"
            />
          </template>
        </div>

        <!-- Get Started Button on the Right -->
        <div class="auth-section">
          <template v-if="!isAuthenticated">
            <!-- Login Button -->
            <a href="#" class="btn btn-secondary" @click.prevent="showLoginModal">
              <i class="fas fa-sign-in-alt"/>
              Sign in
            </a>
            <!-- Primary CTA — changes with the chosen door (persona.js);
                 "Get Started" → /contact when no door is chosen. -->
            <a href="#" class="btn btn-primary get-started-btn" @click.prevent="handleGetStarted">
              <i :class="primaryCta.icon"/>
              <span>{{ primaryCta.label }}</span>
            </a>
          </template>
          <template v-else>
            <!-- Dashboard Button for authenticated users -->
            <a href="#" class="btn btn-primary dashboard-btn" @click.prevent="goToDashboard">
              <i :class="orgLocked ? 'fas fa-lock' : 'fas fa-tachometer-alt'"/>
              {{ orgLocked ? 'Locked' : 'Dashboard' }}
            </a>
            <!-- User Menu -->
            <div class="user-menu">
              <span class="user-greeting" @click="toggleUserDropdown">
                <i class="fas fa-user-circle"/>
                {{ currentUser?.name || currentUser?.email || 'User' }}
                <i class="fas fa-chevron-down" :class="{ 'rotated': showUserDropdown }"/>
              </span>
              <!-- User Dropdown -->
              <div class="user-dropdown" v-if="showUserDropdown" @click.stop>
                <!-- Organizations: only show when the user belongs to >1 org. -->
                <template v-if="organizations.length > 1">
                  <button
                    type="button"
                    class="org-section-toggle"
                    :aria-expanded="orgPanelOpen"
                    @click="toggleOrgPanel"
                  >
                    <span class="org-toggle-label">
                      <i class="fas fa-building"/>
                      <span>Organizations</span>
                      <span class="org-toggle-count">{{ organizations.length }}</span>
                    </span>
                    <span class="org-toggle-current">
                      <span class="org-toggle-name">{{ currentOrg?.name || '—' }}</span>
                      <i class="fas fa-chevron-down" :class="{ 'rotated': orgPanelOpen }"/>
                    </span>
                  </button>

                  <div v-if="orgPanelOpen" class="org-panel">
                    <div v-if="organizations.length > 5" class="org-search-wrap">
                      <i class="fas fa-search"/>
                      <input
                        v-model="orgSearch"
                        type="search"
                        placeholder="Search organizations..."
                        class="org-search"
                        @click.stop
                      >
                    </div>

                    <div class="org-scroll">
                      <div
                        v-for="org in filteredOrganizations"
                        :key="org.id"
                        class="org-row"
                        :class="{ 'is-active': org.is_current, 'is-busy': switchingOrgId === org.id }"
                        role="button"
                        tabindex="0"
                        @click="onSwitchOrganization(org)"
                        @keydown.enter.prevent="onSwitchOrganization(org)"
                      >
                        <span class="org-info">
                          <span class="org-name-row">
                            <span class="org-name">{{ org.name }}</span>
                            <span v-if="org.is_default" class="default-pill" title="Default organization">DEFAULT</span>
                          </span>
                          <span class="org-meta">
                            <span v-if="org.tier" class="org-tier" :class="`tier-${org.tier.toLowerCase()}`">{{ org.tier }}</span>
                            <span v-else-if="org.is_system_org" class="org-tier tier-system">FREE</span>
                            <span class="org-role">{{ org.is_owner ? 'Owner' : (org.role || 'Member') }}</span>
                          </span>
                        </span>
                        <span class="org-actions">
                          <button
                            type="button"
                            class="default-star-btn"
                            :class="{ 'is-default': org.is_default, 'is-busy': defaultingOrgId === org.id }"
                            :title="org.is_default ? 'Default organization' : 'Set as default'"
                            :disabled="defaultingOrgId === org.id || org.is_default"
                            @click.stop="onSetDefault(org)"
                          >
                            <i v-if="defaultingOrgId === org.id" class="fas fa-spinner fa-spin"/>
                            <i v-else-if="org.is_default" class="fas fa-star"/>
                            <i v-else class="far fa-star"/>
                          </button>
                          <i v-if="org.is_current && switchingOrgId !== org.id" class="fas fa-check active-check"/>
                          <i v-else-if="switchingOrgId === org.id" class="fas fa-spinner fa-spin active-check"/>
                          <span v-else class="active-check-spacer"/>
                        </span>
                      </div>
                      <div v-if="!filteredOrganizations.length" class="org-empty">
                        No organizations match "{{ orgSearch }}".
                      </div>
                    </div>
                  </div>

                  <hr class="dropdown-divider">
                </template>

                <a href="#" @click.prevent="goToProfile" class="dropdown-item">
                  <i class="fas fa-user"/>
                  Profile Settings
                </a>
                <!-- Tenant lifecycle: while the active org is locked the menu
                     keeps Profile, Billing, Data export and Log out only
                     (org-lifecycle.js filterNavForLock); the org switcher
                     above stays so a multi-org user can move on. -->
                <router-link
                  v-if="orgLocked"
                  :to="lockedPath"
                  class="dropdown-item"
                  @click="showUserDropdown = false"
                >
                  <i class="fas fa-file-export"/>
                  Data export
                </router-link>
                <a
                  v-if="!orgLocked"
                  href="#"
                  class="dropdown-item"
                  @click.prevent="goToMyPage"
                >
                  <i class="fas fa-cogs"/>
                  My Account
                </a>
                <a
                  v-if="canViewBilling"
                  href="#"
                  @click.prevent="goToBilling"
                  class="dropdown-item"
                >
                  <i class="fas fa-file-invoice-dollar"/>
                  Billing
                </a>
                <a
                  v-if="!orgLocked"
                  href="#"
                  class="dropdown-item"
                  @click.prevent="goToBackups"
                >
                  <i class="fas fa-shield-halved"/>
                  Backups
                </a>
                <a
                  v-if="!orgLocked"
                  href="#"
                  class="dropdown-item"
                  @click.prevent="goToDomains"
                >
                  <i class="fas fa-globe"/>
                  Domains &amp; SSL
                </a>
                <router-link
                  v-if="devEnvNavVisible && !orgLocked"
                  to="/my/dev-environments"
                  class="dropdown-item"
                  :title="devEnvLocked ? 'Ask your operator or organisation admin for dev-environment access' : null"
                  @click="showUserDropdown = false"
                >
                  <i class="fas fa-code"/>
                  Dev Environments
                  <i v-if="devEnvLocked" class="fas fa-lock nav-lock" aria-label="Permission needed"/>
                </router-link>
                <a
                  v-if="!orgLocked"
                  href="#"
                  class="dropdown-item"
                  @click.prevent="goToMembers"
                >
                  <i class="fas fa-users"/>
                  Members
                </a>
                <router-link
                  v-if="isOperator"
                  to="/environments"
                  class="dropdown-item"
                  @click="showUserDropdown = false"
                >
                  <i class="fas fa-diagram-project"/>
                  Platform environments
                </router-link>
                <router-link
                  v-if="isOperator"
                  to="/admin/environments"
                  class="dropdown-item"
                  @click="showUserDropdown = false"
                >
                  <i class="fas fa-layer-group"/>
                  Environments console
                </router-link>
                <a
                  v-if="currentUser?.is_super_admin === true"
                  href="#"
                  @click.prevent="goToClusterDashboard"
                  class="dropdown-item"
                >
                  <i class="fas fa-server"/>
                  Cluster Dashboard
                </a>
                <hr class="dropdown-divider">
                <a href="#" @click.prevent="handleLogout" class="dropdown-item logout">
                  <i class="fas fa-sign-out-alt"/>
                  Log out
                </a>
              </div>
            </div>
          </template>

          <!-- Mobile hamburger — visible only ≤820px -->
          <button
            ref="hamburger"
            type="button"
            class="hamburger"
            :class="{ 'is-open': drawerOpen }"
            :aria-expanded="drawerOpen ? 'true' : 'false'"
            :aria-label="drawerOpen ? 'Close navigation menu' : 'Open navigation menu'"
            aria-controls="mobile-nav-drawer"
            @click="toggleDrawer"
          >
            <span class="hamburger-bar"/>
            <span class="hamburger-bar"/>
            <span class="hamburger-bar"/>
          </button>
        </div>
      </nav>
    </div>

    <!-- Mobile Drawer (rendered alongside the navbar; visibility controlled by CSS @media + drawerOpen) -->
    <transition name="backdrop">
      <div
        v-if="drawerOpen"
        class="drawer-backdrop"
        @click="closeDrawer"
        aria-hidden="true"
      />
    </transition>

    <transition name="drawer">
      <aside
        v-if="drawerOpen"
        id="mobile-nav-drawer"
        ref="drawer"
        class="drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        @keydown.tab="handleDrawerTab"
      >
        <div class="drawer-header">
          <router-link to="/" class="drawer-logo" @click="closeDrawer">
            <img :src="'/media/recraft/logo/denvstack-icon.svg'" alt="DenvStack" class="drawer-logo-icon" >
            <span>DenvStack</span>
          </router-link>
          <button
            ref="drawerClose"
            type="button"
            class="hamburger drawer-close is-open"
            aria-label="Close navigation menu"
            aria-controls="mobile-nav-drawer"
            :aria-expanded="'true'"
            @click="closeDrawer"
          >
            <span class="hamburger-bar"/>
            <span class="hamburger-bar"/>
            <span class="hamburger-bar"/>
          </button>
        </div>

        <nav class="drawer-nav" aria-label="Primary">
          <template v-for="link in primaryNavLinks" :key="link.to || link.href">
            <!-- External links (e.g. Docs) use a plain anchor so a full page
                 load reaches the ingress, not the SPA router. -->
            <a
              v-if="link.external"
              :href="link.href"
              class="drawer-link"
              @click="closeDrawer"
            >
              <span v-html="link.label"/>
            </a>
            <router-link
              v-else
              :to="link.to"
              class="drawer-link"
              @click="closeDrawer"
            >
              <span v-html="link.label"/>
            </router-link>
          </template>
        </nav>

        <hr class="drawer-divider" >

        <div class="drawer-persona">
          <PersonaSwitcher variant="drawer" @picked="closeDrawer" />
        </div>

        <hr class="drawer-divider" >

        <div class="drawer-auth">
          <template v-if="!isAuthenticated">
            <a href="#" class="drawer-link drawer-link--ghost" @click.prevent="onDrawerSignIn">
              <i class="fas fa-sign-in-alt"/>
              <span>Sign in</span>
            </a>
            <a href="#" class="drawer-link drawer-link--primary" @click.prevent="onDrawerGetStarted">
              <i :class="primaryCta.icon"/>
              <span>{{ primaryCta.label }}</span>
            </a>
          </template>
          <template v-else>
            <a href="#" class="drawer-link drawer-link--primary" @click.prevent="onDrawerDashboard">
              <i :class="orgLocked ? 'fas fa-lock' : 'fas fa-tachometer-alt'"/>
              <span>{{ orgLocked ? 'Organisation locked' : 'Dashboard' }}</span>
            </a>
            <router-link
              v-for="item in authedMenuItems"
              :key="item.to"
              :to="item.to"
              class="drawer-link"
              @click="closeDrawer"
            >
              <i :class="item.icon"/>
              <span>{{ item.label }}</span>
              <i v-if="item.locked" class="fas fa-lock nav-lock" aria-label="Permission needed"/>
            </router-link>
            <router-link
              v-if="isOperator"
              to="/environments"
              class="drawer-link"
              @click="closeDrawer"
            >
              <i class="fas fa-diagram-project"/>
              <span>Platform environments</span>
            </router-link>
            <router-link
              v-if="isOperator"
              to="/admin/environments"
              class="drawer-link"
              @click="closeDrawer"
            >
              <i class="fas fa-layer-group"/>
              <span>Environments console</span>
            </router-link>
            <a
              v-if="currentUser?.is_super_admin === true"
              href="#"
              class="drawer-link"
              @click.prevent="onDrawerClusterDashboard"
            >
              <i class="fas fa-server"/>
              <span>Cluster Dashboard</span>
            </a>
            <hr class="drawer-divider" >
            <a href="#" class="drawer-link drawer-link--danger" @click.prevent="onDrawerLogout">
              <i class="fas fa-sign-out-alt"/>
              <span>Log out</span>
            </a>
          </template>
        </div>
      </aside>
    </transition>

    <!-- Full-screen loader shown while switching orgs. `switchingOrgId` stays
         set through the window.location reload that follows a successful
         switch, so the overlay persists over the blank reload moment instead
         of leaving a frozen/empty page. -->
    <transition name="org-switch-fade">
      <div v-if="switchingOrgId"
class="org-switch-overlay"
role="status"
aria-live="polite">
        <div class="org-switch-card">
          <i class="fas fa-circle-notch fa-spin org-switch-spin" aria-hidden="true" />
          <span>Switching organization…</span>
        </div>
      </div>
    </transition>
  </header>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'
import { envConfig, isEnvironmentRegistryEnabled } from '@/utils/env-config'
import { environmentsNavEntries } from '@/utils/customer-environments'
import {
  LOCKED_PATH, isActiveOrgRestricted, filterNavForLock, filterPrimaryLinksForLock
} from '@/utils/org-lifecycle'
import { isPlatformOperatorUser } from '@/utils/wizard-field-cards'
import billingAccess from '@/mixins/billingAccess'
import PersonaSwitcher from '@/components/persona/PersonaSwitcher.vue'
import { usePersona } from '@/utils/persona'
import { readToken, REFRESH_TOKEN_KEY, TOKEN_VALID_SECONDS_KEY } from '@/utils/token-storage'

export default {
  name: 'NavBar',
  components: { PersonaSwitcher },
  mixins: [billingAccess],
  setup() {
    // Shared reactive persona (module-level ref) — the CTA follows the door.
    const { door: personaDoor } = usePersona()
    return { personaDoor }
  },
  data() {
    return {
      isScrolled: false,
      showUserDropdown: false,
      switchingOrgId: null,
      defaultingOrgId: null,
      // Org section is collapsed by default — only the current org's
      // name is visible in the header until the user expands.
      orgPanelOpen: false,
      orgSearch: '',
      // Mobile drawer state
      drawerOpen: false,
      // Cached scroll position so we can restore it when the drawer closes.
      // (We toggle body overflow:hidden while open.)
      lastBodyOverflow: '',
      // Live membership watch (see startMembershipWatch). membershipTimer is
      // the single poller handle — its presence is the duplicate-poller guard.
      // membershipChecking is the per-fetch re-entrancy guard.
      membershipTimer: null,
      membershipChecking: false
    }
  },
  computed: {
    ...mapState('auth', ['isAuthenticated', 'user']),
    ...mapGetters('auth', ['currentUser']),
    // P8 — Dev Environments nav gate. Follows the Billing-mixin precedent: a
    // computed over store state so it recomputes when the probe resolves /
    // re-runs on org switch. `navVisible` is true when the platform HAS the
    // feature (probe → available OR forbidden); `isForbidden` renders the lock
    // glyph so the missing grant is discoverable rather than silently hidden.
    // Aliased to avoid ambiguous `navVisible`/`isForbidden` names in a 1900-line
    // component.
    ...mapGetters('devEnvironments', {
      devEnvNavVisible: 'navVisible',
      devEnvLocked: 'isForbidden'
    }),
    /** Active org locked / scheduled for deletion (tenant lifecycle). */
    orgLocked() {
      return this.isAuthenticated && isActiveOrgRestricted(this.currentUser)
    },
    lockedPath() {
      return LOCKED_PATH
    },
    organizations() {
      const list = this.currentUser?.organizations
      return Array.isArray(list) ? list : []
    },
    currentOrg() {
      return this.organizations.find((o) => o.is_current) || null
    },
    /**
     * TRUE when the user administers the active org — drives the org-admin-only
     * "Governance" nav entry. Super-admin always qualifies; otherwise the active
     * org's owner or an "Admin"-role member. Best-effort client-side gate; the
     * /my/deploy-governance endpoints are still org-admin-enforced server-side.
     */
    isOrgAdmin() {
      const u = this.currentUser || this.user || {}
      if (u.is_super_admin === true || u.is_admin === true) return true
      const org = this.currentOrg ||
        this.user?.current_organization ||
        this.user?.default_organization
      if (!org) return false
      return org.is_owner === true ||
        String(org.role || '').toLowerCase() === 'admin'
    },
    /**
     * TRUE when the user is a platform operator — drives the operator-only
     * "Environments" console entry. Super-admin or a platform admin qualifies.
     * The /admin/environments view re-checks this server-side-backed flag and
     * renders an access state for non-operators.
     */
    isOperator() {
      return isPlatformOperatorUser(this.currentUser || this.user)
    },
    /**
     * Platform Environment Registry (P1) dark flag. Gates the new
     * /my/certificates nav entries. Default OFF → the live nav is unchanged
     * until VUE_APP_ENVIRONMENT_REGISTRY_ENABLED is enabled.
     */
    registryEnabled() {
      return isEnvironmentRegistryEnabled()
    },
    /**
     * The header's primary call to action follows the chosen door:
     * owners → marketplace, agencies → sign-up, developers → docs (full load),
     * engineering leads → pricing, self-managed → contact. No door → the
     * original "Get Started" → /contact.
     */
    primaryCta() {
      const d = this.personaDoor
      if (!d) return { label: 'Get Started', to: '/contact', icon: 'fas fa-rocket', external: false }
      return { label: this.$t(d.cta.labelKey), to: d.cta.to, icon: d.cta.icon, external: Boolean(d.cta.external) }
    },
    filteredOrganizations() {
      const q = this.orgSearch.trim().toLowerCase()
      if (!q) return this.organizations
      return this.organizations.filter((o) =>
        (o.name || '').toLowerCase().includes(q) ||
        (o.tier || '').toLowerCase().includes(q) ||
        (o.role || '').toLowerCase().includes(q)
      )
    },
    /**
     * Single source of truth for the primary nav links. The desktop
     * `.nav-links` row and the mobile drawer both v-for over this list,
     * so they stay in sync. `&nbsp;` is preserved for "My Apps" via v-html.
     */
    primaryNavLinks() {
      const links = [
        { to: '/', label: 'Home' },
        { to: '/pricing', label: 'Pricing' },
        { to: '/marketplace', label: 'Marketplace' }
      ]
      if (this.isAuthenticated) {
        links.push({ to: '/my/apps', label: 'My&nbsp;Apps' })
        // The customer WORKSPACE — a single entry whose tabs host
        // Overview / Clusters / Files / Integrations / Governance. It replaces
        // the former standalone Clusters/Files/Integrations/Governance links.
        // Visible to any authenticated member; per-tab content self-gates
        // (e.g. Governance shows an org-admin-required state for non-admins).
        // (URL stays /my/assets to keep existing links working.)
        links.push({ to: '/my/assets', label: 'Workspace' })
        // Environments (owner decision 2026-10-22, reversing 2026-10-21 for
        // CUSTOMER environments): "apps live in environments, every
        // organisation has one for free, more cost money". Every signed-in
        // customer gets "Environments" → /my/environments (their own
        // organisation's, default first). PLATFORM environments stay an
        // operator's concern: "Platform environments" → /environments only
        // for operators, and the console (/admin/environments) stays in the
        // user menu. Decided in utils/customer-environments.js so the gate is
        // unit-tested.
        environmentsNavEntries({ isAuthenticated: true, isOperator: this.isOperator })
          .filter((entry) => entry.placement === 'primary')
          .forEach((entry) => links.push({ to: entry.to, label: entry.label }))
      }
      // Docs is served by platform-dashboard at /docs through the ingress, NOT
      // by this SPA. Mark it external so it renders as a plain <a href> and a
      // full page load reaches the ingress instead of the client-side router.
      links.push({ href: '/docs', label: 'Docs', external: true })
      links.push({ to: '/contact', label: 'Contact' })
      // Locked org: the public pages stay, the app surfaces go.
      return filterPrimaryLinksForLock(links, this.currentUser)
    },
    /**
     * Authenticated user's flat menu items for the drawer. Mirrors the
     * desktop dropdown items 1:1 (minus the org switcher which only makes
     * sense as a contextual popover; the user can still switch orgs from
     * the desktop dropdown). Cluster Dashboard is rendered inline in the
     * template because it requires SSO token handling, not router-link.
     */
    authedMenuItems() {
      const items = [
        { to: '/changeprofile', label: 'Profile Settings', icon: 'fas fa-user' },
        { to: '/my/page', label: 'My Account', icon: 'fas fa-cogs' }
      ]
      // Billing is gated on the current org's view-billing grant, so a
      // technical org-admin with no billing permission never sees the entry
      // point. Recomputes on org switch via the billingAccess mixin.
      if (this.canViewBilling) {
        items.push({ to: '/my/billing', label: 'Billing', icon: 'fas fa-file-invoice-dollar' })
      }
      items.push({ to: '/my/backups', label: 'Backups', icon: 'fas fa-shield-halved' })
      // Domains & SSL — certificates live on the SSL tab (Phase 6.5, P-25).
      items.push({ to: '/my/domains', label: 'Domains & SSL', icon: 'fas fa-globe' })
      // Dev Environments — capability-probed (hidden when the platform flag is
      // off, locked when the caller lacks the grant). Mirrors the desktop item.
      if (this.devEnvNavVisible) {
        items.push({
          to: '/my/dev-environments',
          label: 'Dev Environments',
          icon: 'fas fa-code',
          locked: this.devEnvLocked
        })
      }
      items.push({ to: '/admin/members', label: 'Members', icon: 'fas fa-users' })
      if (this.orgLocked) {
        items.push({ to: LOCKED_PATH, label: 'Data export', icon: 'fas fa-file-export' })
        return filterNavForLock(items, this.currentUser)
      }
      return items
    }
  },
  watch: {
    // Lazy-load /me the first time the user appears authenticated so the
    // org switcher has data to render. fetchMe is idempotent.
    isAuthenticated: {
      immediate: true,
      handler(authed) {
        if (authed) {
          if (!this.organizations.length) {
            this.$store.dispatch('auth/fetchMe')
          }
          // P8 — resolve the Dev Environments capability alongside /me.
          // Fire-and-forget: the store de-dupes in-flight probes and classifies
          // every failure (503 disabled / 403 forbidden / 503 unavailable), so
          // this never rejects into the watcher.
          this.$store.dispatch('devEnvironments/probe')
          // Begin watching for server-side membership changes. Idempotent —
          // start guards against a second poller.
          this.startMembershipWatch()
        } else {
          // Logged out → tear down the poller and its listeners.
          this.stopMembershipWatch()
          this.$store.dispatch('devEnvironments/reset')
        }
      }
    },
    // Cheap opportunistic refresh on navigation: reconcileMembership is
    // guarded (single fetch in flight, no recursion) so this just catches an
    // out-of-band removal sooner when the user moves into an org-scoped view.
    $route() {
      if (this.isAuthenticated) this.reconcileMembership()
    }
  },
  methods: {
    ...mapMutations(['setShowAuthModal']),
    ...mapActions('auth', ['logout', 'switchOrganization', 'setDefaultOrganization']),

    toggleOrgPanel() {
      this.orgPanelOpen = !this.orgPanelOpen
      if (!this.orgPanelOpen) this.orgSearch = ''
    },

    async onSwitchOrganization(org) {
      if (!org || org.is_current || this.switchingOrgId) return
      this.switchingOrgId = org.id
      try {
        await this.switchOrganization(org.id)
        this.showUserDropdown = false
        this.orgPanelOpen = false
        this.orgSearch = ''
        // Full reload so EVERY view re-downloads the new org's data with the
        // new org-scoped token (My Apps, Assets, Clusters, Files, Dashboard,
        // …). The new access token was already persisted to storage by
        // SET_AUTH_DATA, so the reload boots straight into the new org. A soft
        // per-store refresh is too easy to leave a stale corner (the bug that
        // showed the previous org's apps after switching), so we reset the
        // whole browser state. If we're on a resource DETAIL page that belonged
        // to the old org, drop to that section's index so the reload doesn't
        // land on a 403; otherwise reload in place.
        const path = this.$route.path
        const detailSection = ['/my/apps/', '/my/files/', '/my/dev-environments/', '/clusters/', '/assets/']
          .find((p) => path.startsWith(p) && path.length > p.length)
        if (detailSection) {
          window.location.assign(detailSection.replace(/\/$/, ''))
        } else {
          window.location.reload()
        }
      } catch (e) {
        // showNotification already fired inside the action.
        this.switchingOrgId = null
      }
    },

    async onSetDefault(org) {
      if (!org || org.is_default || this.defaultingOrgId) return
      this.defaultingOrgId = org.id
      try {
        await this.setDefaultOrganization(org.id)
        this.$store.dispatch('showNotification', {
          type: 'success',
          message: `${org.name} is now your default organization. You'll land here on next login.`
        })
      } catch (e) {
        // showNotification already fired inside the action.
      } finally {
        this.defaultingOrgId = null
      }
    },

    /* --------------------- Live membership watch --------------------- */
    // A super-admin can remove this user from an org server-side. Without a
    // live signal the org switcher keeps showing the stale org until a manual
    // reload. We poll /me on a light interval + on tab-focus, then reconcile
    // the org list: if the org the user is *currently viewing* vanished we
    // switch them to a remaining org (or log them out if none remain).

    // Start the watcher exactly once. Idempotent: guarded by membershipTimer
    // so re-entrant calls (e.g. the isAuthenticated watcher firing alongside
    // mounted) never spin up a second poller.
    startMembershipWatch() {
      if (this.membershipTimer) return
      // 30s light poll. fetchMe is cheap and idempotent; reconcileMembership
      // has its own in-flight guard so overlapping ticks are a no-op.
      this.membershipTimer = window.setInterval(() => {
        this.reconcileMembership()
      }, 30000)
      // Refresh the moment the user returns to this tab so switching back
      // picks up an out-of-band removal immediately rather than waiting for
      // the next interval tick.
      window.addEventListener('visibilitychange', this.onVisibilityRefresh)
      window.addEventListener('focus', this.onVisibilityRefresh)
    },

    stopMembershipWatch() {
      if (this.membershipTimer) {
        window.clearInterval(this.membershipTimer)
        this.membershipTimer = null
      }
      window.removeEventListener('visibilitychange', this.onVisibilityRefresh)
      window.removeEventListener('focus', this.onVisibilityRefresh)
    },

    onVisibilityRefresh() {
      // visibilitychange fires for both hide and show; only act on show.
      if (document.visibilityState && document.visibilityState !== 'visible') return
      // Every switch between DevTools and the page fires `focus`; with the
      // 30 s poll already running, a refresh more often than every 10 s is
      // only a /me storm (20+ identical calls in a minute, 2026-10-08).
      const now = Date.now()
      if (now - (this._lastFocusRefresh || 0) < 10000) return
      this._lastFocusRefresh = now
      this.reconcileMembership()
    },

    // Re-fetch /me, diff the org-id set before vs after, and react to any
    // change that affects the org the user is currently viewing.
    async reconcileMembership() {
      if (!this.isAuthenticated) return
      // In-flight guard: prevents overlapping fetches (poll tick + focus +
      // route nav can all coincide) and any chance of a fetch loop.
      if (this.membershipChecking) return
      this.membershipChecking = true

      // Snapshot BEFORE the fetch: the set of org ids the user belonged to,
      // and which org they were actively viewing (is_current).
      const beforeOrgs = this.organizations
      const beforeIds = new Set(beforeOrgs.map((o) => o.id))
      const activeOrg = this.currentOrg
      const activeId = activeOrg ? activeOrg.id : null

      try {
        // fetchMe commits MERGE_USER; it does NOT itself dispatch another
        // fetch, so there is no recursion here.
        const me = await this.$store.dispatch('auth/fetchMe')
        if (!me) return // network blip — leave the UI untouched, try next tick

        const afterOrgs = this.organizations
        const afterIds = new Set(afterOrgs.map((o) => o.id))

        // Nothing we tracked disappeared → the switcher list already
        // re-rendered from the fresh /me; we're done.
        const removed = [...beforeIds].filter((id) => !afterIds.has(id))
        if (!removed.length) return

        // Was the org they're *viewing* one of the removed ones?
        const lostActive = activeId != null && !afterIds.has(activeId)
        if (!lostActive) {
          // A non-current org disappeared — the list update is enough.
          // Subtle, non-disruptive heads-up.
          this.$store.dispatch('showNotification', {
            type: 'info',
            message: 'Your organization access was updated.'
          })
          return
        }

        const oldName = (activeOrg && activeOrg.name) || 'your organization'

        if (afterOrgs.length === 0) {
          // No orgs left at all → can't stay signed in to an org-scoped portal.
          this.$store.dispatch('showNotification', {
            type: 'warning',
            message: `You were removed from "${oldName}".`,
            timeout: 7000
          })
          await this.logout()
          // logout() routes off auth-required pages; ensure we land on login.
          if (this.$route.path !== '/') {
            this.$router.push('/').catch(() => {})
          }
          return
        }

        // Other orgs remain → move the user onto one of them. Prefer the
        // org /me now marks current; otherwise the default; otherwise first.
        const target = afterOrgs.find((o) => o.is_current) ||
          afterOrgs.find((o) => o.is_default) ||
          afterOrgs[0]

        // Only issue a switch if the backend hasn't already re-pointed the
        // session at a valid org (avoids a redundant token re-issue).
        if (!afterOrgs.some((o) => o.is_current)) {
          try {
            await this.switchOrganization(target.id)
          } catch (e) {
            // switchOrganization surfaces its own error toast.
          }
        }

        this.$store.dispatch('showNotification', {
          type: 'info',
          message: `You were removed from "${oldName}". Switched to "${target.name}".`,
          timeout: 7000
        })

        // Bounce off any org-scoped view so they aren't stuck looking at a
        // workspace they no longer belong to.
        if (this.$route.path.startsWith('/admin') || this.$route.path.startsWith('/my')) {
          this.$router.push('/my/apps').catch(() => {})
        }
      } catch (e) {
        // Swallow — a failed reconcile must never break the nav. Next tick retries.
        console.warn('Membership reconcile failed:', e?.message || e)
      } finally {
        this.membershipChecking = false
      }
    },

    handleScroll() {
      this.isScrolled = window.scrollY > 50
    },

    showLoginModal() {
      this.setShowAuthModal(true)
    },

    toggleUserDropdown() {
      this.showUserDropdown = !this.showUserDropdown
    },

    handleGetStarted() {
      // Door-aware primary CTA (see primaryCta). External targets (Docs is
      // served by platform-dashboard through the ingress) need a full load.
      const cta = this.primaryCta
      if (cta.external) {
        window.location.href = cta.to
        return
      }
      this.$router.push(cta.to)
    },

    goToDashboard() {
      this.showUserDropdown = false
      this.$router.push(this.orgLocked ? LOCKED_PATH : '/admin')
    },

    goToClusterDashboard() {
      this.showUserDropdown = false

      // Get current authentication tokens
      // readToken(), not localStorage directly: a Remember-me=off session
      // keeps its tokens in sessionStorage, so the direct read sent an empty
      // `refresh=` — and on an origin holding two sessions it could read the
      // OTHER one's refresh token into this SSO hand-off. readToken() checks
      // both storages and fails closed when they disagree.
      const accessToken = this.$store.getters['auth/accessToken']
      const refreshToken = this.$store.getters['auth/refreshToken'] || readToken(REFRESH_TOKEN_KEY)
      const tokenValidSeconds = this.$store.state.auth?.tokenValidSeconds || readToken(TOKEN_VALID_SECONDS_KEY)

      if (!accessToken) {
        console.error('No access token found, cannot redirect to cluster dashboard')
        this.setShowAuthModal(true)
        return
      }

      // Get cluster dashboard URL from environment configuration (single source of truth).
      // Normalize: when the dashboard is hosted under a sub-path, its Vite/Nginx
      // base config can reject requests that hit the bare path without a trailing
      // slash before the query string. Ensure the trailing slash before appending
      // the SSO token query string so it works regardless of how the env value
      // is written.
      const rawDashboardURL = envConfig.VUE_APP_CLUSTER_DASHBOARD_URL || ''
      const dashboardBaseURL = rawDashboardURL.endsWith('/') ? rawDashboardURL : `${rawDashboardURL}/`

      // Construct URL with authentication tokens for SSO
      const dashboardURLWithToken = `${dashboardBaseURL}?token=${encodeURIComponent(accessToken)}&refresh=${encodeURIComponent(refreshToken || '')}&token_valid_seconds=${encodeURIComponent(tokenValidSeconds || '')}`

      console.log('Redirecting to cluster dashboard:', dashboardBaseURL)

      // Redirect to cluster dashboard with SSO tokens
      window.location.href = dashboardURLWithToken
    },

    // @deprecated — admin-web is being retired; use goToClusterDashboard for super-admin SSO.
    // Left in place to avoid breaking any caller we may have missed; remove once
    // a sweep confirms nothing references it.
    redirectToAdminPanel() {
      // Get current authentication tokens
      // readToken(), not localStorage directly: a Remember-me=off session
      // keeps its tokens in sessionStorage, so the direct read sent an empty
      // `refresh=` — and on an origin holding two sessions it could read the
      // OTHER one's refresh token into this SSO hand-off. readToken() checks
      // both storages and fails closed when they disagree.
      const accessToken = this.$store.getters['auth/accessToken']
      const refreshToken = this.$store.getters['auth/refreshToken'] || readToken(REFRESH_TOKEN_KEY)
      const tokenValidSeconds = this.$store.state.auth?.tokenValidSeconds || readToken(TOKEN_VALID_SECONDS_KEY)

      if (!accessToken) {
        console.error('No access token found, cannot redirect to admin panel')
        this.setShowAuthModal(true)
        return
      }

      // Get admin panel URL from environment configuration (single source of truth).
      // Normalize: when admin-web is hosted under a sub-path (e.g. /admin/), its
      // Vite/Nginx is configured with `base: '/admin/'` and rejects requests that
      // hit the bare path without a trailing slash (`/admin?token=…`). Ensure the
      // trailing slash before appending the query string so SSO works regardless
      // of how the env value is written.
      const rawAdminURL = envConfig.VUE_APP_ADMIN_PANEL_URL || ''
      const adminPanelBaseURL = rawAdminURL.endsWith('/') ? rawAdminURL : `${rawAdminURL}/`

      // Construct URL with authentication tokens for SSO
      const adminPanelURLWithToken = `${adminPanelBaseURL}?token=${encodeURIComponent(accessToken)}&refresh=${encodeURIComponent(refreshToken || '')}&token_valid_seconds=${encodeURIComponent(tokenValidSeconds || '')}`

      console.log('Redirecting to admin panel:', adminPanelBaseURL)

      // Redirect to admin panel with SSO tokens
      window.location.href = adminPanelURLWithToken
    },

    goToProfile() {
      this.showUserDropdown = false
      this.$router.push('/changeprofile')
    },

    goToMyPage() {
      this.showUserDropdown = false
      this.$router.push('/my/page')
    },

    goToMembers() {
      this.showUserDropdown = false
      this.$router.push('/admin/members')
    },

    goToBilling() {
      this.showUserDropdown = false
      this.$router.push('/my/billing')
    },

    goToBackups() {
      this.showUserDropdown = false
      this.$router.push('/my/backups')
    },

    goToDomains() {
      this.showUserDropdown = false
      this.$router.push('/my/domains')
    },

    /** P8 — drop the cached capability/list and probe again for the new org. */
    reprobeDevEnvironments() {
      if (!this.isAuthenticated) return
      this.$store.dispatch('devEnvironments/reset')
      this.$store.dispatch('devEnvironments/probe')
    },

    async handleLogout() {
      try {
        this.showUserDropdown = false
        await this.logout()
      } catch (error) {
        console.error('Logout error:', error)
      }
    },

    // Close dropdown when clicking outside
    handleOutsideClick(event) {
      if (!this.$el.contains(event.target)) {
        this.showUserDropdown = false
      }
    },

    /* ------------------------- Mobile drawer ------------------------- */

    toggleDrawer() {
      if (this.drawerOpen) this.closeDrawer()
      else this.openDrawer()
    },

    openDrawer() {
      if (this.drawerOpen) return
      // Close the desktop user dropdown if it happened to be open — the
      // drawer is the mobile equivalent and we don't want both layered.
      this.showUserDropdown = false
      this.drawerOpen = true
      // Lock body scroll so the page underneath doesn't scroll while the
      // drawer is open. Cache the previous value to restore it cleanly.
      this.lastBodyOverflow = document.body.style.overflow || ''
      document.body.style.overflow = 'hidden'
    },

    closeDrawer() {
      if (!this.drawerOpen) return
      this.drawerOpen = false
      document.body.style.overflow = this.lastBodyOverflow
      // Return focus to the hamburger trigger for keyboard users.
      this.$nextTick(() => {
        if (this.$refs.hamburger && typeof this.$refs.hamburger.focus === 'function') {
          this.$refs.hamburger.focus()
        }
      })
    },

    onDrawerSignIn() {
      this.closeDrawer()
      this.setShowAuthModal(true)
    },

    onDrawerGetStarted() {
      this.closeDrawer()
      this.handleGetStarted()
    },

    onDrawerDashboard() {
      this.closeDrawer()
      this.$router.push(this.orgLocked ? LOCKED_PATH : '/admin')
    },

    onDrawerClusterDashboard() {
      this.closeDrawer()
      this.goToClusterDashboard()
    },

    async onDrawerLogout() {
      this.closeDrawer()
      try {
        await this.logout()
      } catch (error) {
        console.error('Logout error:', error)
      }
    },

    /**
     * Minimal focus trap: when Tab/Shift+Tab would move focus out of the
     * drawer, wrap to the other end. Keeps keyboard users contained while
     * the modal drawer is open without pulling in a dependency.
     */
    handleDrawerTab(event) {
      const root = this.$refs.drawer
      if (!root) return
      const focusables = root.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input, select, textarea'
      )
      if (!focusables.length) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      const active = document.activeElement

      if (event.shiftKey) {
        if (active === first || !root.contains(active)) {
          event.preventDefault()
          last.focus()
        }
      } else {
        if (active === last) {
          event.preventDefault()
          first.focus()
        }
      }
    },

    handleEscape(event) {
      if (event.key === 'Escape' && this.drawerOpen) {
        this.closeDrawer()
      }
    },

    handleResize() {
      // If user crosses back to desktop while drawer is open, close it
      // so the desktop nav reappears cleanly with no orphaned overlay.
      if (this.drawerOpen && window.innerWidth > 820) {
        this.closeDrawer()
      }
    }
  },
  mounted() {
    console.log('NavBar mounted successfully')
    console.log('Auth state:', this.isAuthenticated)

    window.addEventListener('scroll', this.handleScroll)
    document.addEventListener('click', this.handleOutsideClick)
    document.addEventListener('keydown', this.handleEscape)
    window.addEventListener('resize', this.handleResize)
    // P8 — an org switch changes the caller's grants, so re-probe the Dev
    // Environments capability (same event the /my/* views already listen to).
    window.addEventListener('cs:org-switched', this.reprobeDevEnvironments)
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll)
    document.removeEventListener('click', this.handleOutsideClick)
    document.removeEventListener('keydown', this.handleEscape)
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('cs:org-switched', this.reprobeDevEnvironments)
    // Tear down the membership poller + its focus/visibility listeners.
    this.stopMembershipWatch()
    // Defensive: if the component unmounts with the drawer open (e.g. HMR),
    // make sure body scroll is restored.
    if (this.drawerOpen) {
      document.body.style.overflow = this.lastBodyOverflow
    }
  }
}
</script>

<style scoped>
.navbar {
  background: var(--ds-white);
  position: fixed;
  width: 100%;
  top: 0;
  left: 0;
  right: 0;
  z-index: 999999;
  padding: 0;
  transition: all 0.3s ease;
  border-bottom: 1px solid var(--ds-neutral-200);
  box-shadow: var(--ds-shadow-sm);
  min-height: 70px;
  display: block;
  visibility: visible;
  opacity: 1;
}

/* Full-width inner row: edge-to-edge with symmetric viewport gutters.
 * Drops the legacy max-width:1200px wrapper that made the row look
 * squeezed inside a narrow centred column on >1280px displays. */
.navbar-inner {
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 100%;
  margin: 0;
  padding-inline: clamp(16px, 3vw, 48px);
  position: relative;
  height: 70px;
}

.navbar.scrolled {
  box-shadow: var(--ds-shadow-md);
}

.nav-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 100%;
}

.logo {
  font-size: 1.5rem;
  font-weight: 700;
  font-family: var(--ds-font-display);
  color: var(--ds-primary-900);
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.logo:hover {
  color: var(--ds-primary-500);
  transform: translateY(-1px);
}

.logo-icon {
  width: 36px;
  height: 36px;
  object-fit: contain;
}

.logo span {
  color: inherit;
}

/* Persona switcher — sits beside the logo; the drawer owns it ≤820px. */
.nav-persona {
  margin-left: clamp(8px, 1.2vw, 20px);
  flex-shrink: 1;
  min-width: 0;
}

@media (max-width: 1200px) {
  /* Value only ("Developer ▾") once the row tightens. */
  .nav-persona :deep(.pw-trigger-label) { display: none; }
}

.drawer-persona { padding: 0.25rem 0.25rem 0.5rem; }

/* Navigation Links */
.nav-links {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: clamp(2px, 0.6vw, 10px);
  margin-left: auto;
  margin-right: auto;
  min-width: 0;
}

.nav-link {
  color: var(--ds-neutral-700);
  text-decoration: none;
  font-weight: 500;
  font-size: 0.95rem;
  padding: 0.5rem clamp(0.55rem, 0.9vw, 1rem);
  border-radius: var(--ds-radius-sm);
  transition: all 0.3s ease;
  position: relative;
  white-space: nowrap;
  flex-shrink: 0;
}

.nav-link:hover {
  color: var(--ds-primary-500);
  background: var(--ds-primary-100);
  text-decoration: none;
}

.nav-link.router-link-active {
  color: var(--ds-primary-500);
  font-weight: 600;
}

.nav-link.router-link-active::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 50%;
  transform: translateX(-50%);
  width: 60%;
  height: 2px;
  background: var(--ds-primary-500);
  border-radius: 2px;
}

.auth-section {
  display: flex;
  align-items: center;
  gap: clamp(6px, 0.75vw, 12px);
  flex-shrink: 0;
  white-space: nowrap;
}

/* Get Started Button */
.get-started-btn {
  position: relative;
  overflow: hidden;
  font-weight: 600;
  font-size: 0.95rem;
  padding: 0.7rem 1.75rem;
  border-radius: var(--ds-radius-md);
  border: none;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: var(--ds-primary-500);
  color: var(--ds-white);
  box-shadow: var(--ds-shadow-sm);
  transition: all 0.3s ease;
}

.get-started-btn:hover {
  background: var(--ds-primary-400);
  transform: translateY(-2px);
  box-shadow: var(--ds-shadow-md);
  color: var(--ds-white);
  text-decoration: none;
}

.get-started-btn i {
  font-size: 0.9rem;
  color: var(--ds-white);
}

/* Dashboard Button — kept (only entry-point for non-super-admin
 * users), but tightened so it doesn't crowd the nav row. */
.dashboard-btn {
  position: relative;
  overflow: hidden;
  font-weight: 600;
  font-size: 0.88rem;
  padding: 0.5rem 1rem;
  border-radius: var(--ds-radius-md);
  border: none;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  background: var(--ds-primary-500);
  color: var(--ds-white);
  box-shadow: var(--ds-shadow-sm);
  transition: all 0.3s ease;
  margin-right: 0;
  white-space: nowrap;
  flex-shrink: 0;
}

.dashboard-btn:hover {
  background: var(--ds-primary-400);
  transform: translateY(-2px);
  box-shadow: var(--ds-shadow-md);
  color: var(--ds-white);
  text-decoration: none;
}

.user-menu {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
  min-width: 0;
}

.user-greeting {
  color: var(--ds-neutral-700);
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  padding: 0.5rem 0.75rem;
  border-radius: var(--ds-radius-md);
  transition: all 0.3s ease;
  /* Prevent a long email from elbowing the nav into wrap territory. */
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-greeting:hover {
  background: var(--ds-neutral-100);
  color: var(--ds-primary-500);
}

.user-greeting .fa-chevron-down {
  font-size: 0.75rem;
  transition: transform 0.3s ease;
}

.user-greeting .fa-chevron-down.rotated {
  transform: rotate(180deg);
}

.user-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: var(--ds-white);
  border-radius: var(--ds-radius-lg);
  box-shadow: var(--ds-shadow-lg);
  border: 1px solid var(--ds-neutral-200);
  min-width: 260px;
  z-index: 1000;
  overflow: hidden;
}

.org-section-toggle {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1.25rem;
  background: transparent;
  border: none;
  cursor: pointer;
  font: inherit;
  text-align: left;
  color: var(--ds-neutral-700);
  transition: background 0.15s ease;
}

.org-section-toggle:hover {
  background: var(--ds-neutral-100);
}

.org-toggle-label {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--ds-neutral-500);
  flex-shrink: 0;
}

.org-toggle-label i {
  color: var(--ds-primary-500);
  font-size: 0.8rem;
}

.org-toggle-count {
  background: var(--ds-neutral-200);
  color: var(--ds-neutral-700);
  border-radius: 999px;
  padding: 1px 7px;
  font-size: 0.65rem;
  letter-spacing: 0.02em;
}

.org-toggle-current {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  flex: 0 1 auto;
}

.org-toggle-name {
  color: var(--ds-neutral-900);
  font-size: 0.85rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 130px;
}

.org-toggle-current .fa-chevron-down {
  font-size: 0.7rem;
  color: var(--ds-neutral-500);
  transition: transform 0.2s ease;
}

.org-toggle-current .fa-chevron-down.rotated {
  transform: rotate(180deg);
}

.org-panel {
  background: #fafbfc;
  border-top: 1px solid var(--ds-neutral-200);
  border-bottom: 1px solid var(--ds-neutral-200);
  padding: 0.5rem 0;
}

.org-search-wrap {
  position: relative;
  padding: 0 1rem 0.5rem;
}

.org-search-wrap i {
  position: absolute;
  top: 50%;
  left: 1.6rem;
  transform: translateY(-50%);
  color: var(--ds-neutral-500);
  font-size: 0.8rem;
  pointer-events: none;
}

.org-search {
  width: 100%;
  padding: 0.45rem 0.65rem 0.45rem 1.95rem;
  border: 1px solid var(--ds-neutral-200);
  border-radius: 8px;
  font-size: 0.85rem;
  font-family: inherit;
  background: white;
  color: var(--ds-neutral-900);
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.org-search:focus {
  border-color: var(--ds-primary-500);
  box-shadow: 0 0 0 3px rgba(26, 127, 212, 0.12);
}

.org-scroll {
  max-height: 280px;
  overflow-y: auto;
  padding: 0 0.25rem;
}

.org-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.55rem 0.95rem;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease;
  margin: 1px 0;
}

.org-row:hover,
.org-row:focus-visible {
  background: rgba(26, 127, 212, 0.06);
  outline: none;
}

.org-row.is-active {
  background: rgba(26, 127, 212, 0.1);
}

.org-row.is-busy {
  opacity: 0.6;
  cursor: progress;
}

.org-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1 1 auto;
  min-width: 0;
}

.org-name-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.org-name {
  font-weight: 600;
  color: var(--ds-neutral-900);
  font-size: 0.9rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.default-pill {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 4px;
  background: #fffbeb;
  color: #b45309;
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  flex-shrink: 0;
}

.org-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.72rem;
}

.org-tier {
  display: inline-block;
  padding: 1px 7px;
  border-radius: 999px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: #eef2ff;
  color: #4338ca;
}

.org-tier.tier-starter { background: #ecfeff; color: #0e7490; }
.org-tier.tier-pro { background: #fef3c7; color: #92400e; }
.org-tier.tier-business { background: #fce7f3; color: #9d174d; }
.org-tier.tier-licensed { background: #d6f5ef; color: #0f766e; }
.org-tier.tier-system { background: #f1f5f9; color: #475569; }

.org-role {
  color: var(--ds-neutral-500);
  font-weight: 500;
}

.org-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  flex-shrink: 0;
}

.default-star-btn {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: var(--ds-neutral-300);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  transition: color 0.15s ease, background 0.15s ease;
}

.default-star-btn:hover:not(:disabled) {
  color: #d97706;
  background: #fffbeb;
}

.default-star-btn.is-default {
  color: #d97706;
  cursor: default;
}

.default-star-btn:disabled {
  cursor: default;
}

.default-star-btn.is-busy {
  color: var(--ds-primary-500);
}

.active-check {
  color: var(--ds-primary-500);
  font-size: 0.8rem;
  width: 14px;
  text-align: center;
}

.active-check-spacer {
  display: inline-block;
  width: 14px;
}

.org-empty {
  padding: 0.75rem 1rem;
  font-size: 0.82rem;
  color: var(--ds-neutral-500);
  text-align: center;
  font-style: italic;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.25rem;
  color: var(--ds-neutral-700);
  text-decoration: none;
  transition: all 0.2s ease;
  font-size: 0.9rem;
}

.dropdown-item:hover {
  background: var(--ds-neutral-100);
  color: var(--ds-primary-500);
  text-decoration: none;
}

.dropdown-item.logout:hover {
  background: #fef2f2;
  color: var(--ds-danger);
}

/* P8 — small lock glyph on a capability-gated entry (Dev Environments when the
 * caller lacks the grant). Pushed to the row's right edge in both the desktop
 * dropdown and the mobile drawer. */
.nav-lock {
  margin-left: auto;
  font-size: 0.7rem;
  color: var(--ds-neutral-500);
  opacity: 0.85;
}

.dropdown-divider {
  margin: 0.25rem 0;
  border: none;
  border-top: 1px solid var(--ds-neutral-200);
}

/* ===================================================================
 * Mobile Hamburger + Drawer
 * Desktop (≥821px): hamburger is display:none; drawer is never rendered.
 * Mobile (≤820px): hamburger appears at the right edge; drawer slides in.
 * =================================================================== */

.hamburger {
  /* Hidden on desktop — the inverse of .nav-links which hides ≤820px. */
  display: none;
  width: 44px;
  height: 44px;
  padding: 0;
  margin-left: 4px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--ds-radius-md);
  cursor: pointer;
  position: relative;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  flex-shrink: 0;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.hamburger:hover {
  background: var(--ds-neutral-100);
}

.hamburger:focus-visible {
  outline: none;
  border-color: var(--ds-primary-500);
  box-shadow: 0 0 0 3px rgba(26, 127, 212, 0.18);
}

.hamburger-bar {
  display: block;
  width: 22px;
  height: 2px;
  background: var(--ds-primary-900, #0a2540);
  border-radius: 2px;
  transition:
    transform 0.25s ease,
    opacity 0.2s ease,
    background 0.2s ease;
  transform-origin: center;
}

/* Three-line → X transform */
.hamburger.is-open .hamburger-bar:nth-child(1) {
  transform: translateY(7px) rotate(45deg);
}
.hamburger.is-open .hamburger-bar:nth-child(2) {
  opacity: 0;
  transform: scaleX(0.2);
}
.hamburger.is-open .hamburger-bar:nth-child(3) {
  transform: translateY(-7px) rotate(-45deg);
}

@media (prefers-reduced-motion: reduce) {
  .hamburger-bar {
    transition: none;
  }
}

/* Backdrop */
.drawer-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(10, 37, 64, 0.5);
  z-index: 998;
  /* Above page content but below the drawer panel itself. */
}

.backdrop-enter-active,
.backdrop-leave-active {
  transition: opacity 0.2s ease;
}
.backdrop-enter-from,
.backdrop-leave-to {
  opacity: 0;
}

/* Drawer panel */
.drawer {
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  height: 100dvh;
  width: min(85vw, 360px);
  background: #fff;
  border-left: 1px solid var(--ds-neutral-200);
  box-shadow: -12px 0 32px -8px rgba(10, 37, 64, 0.18);
  z-index: 999;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.drawer-enter-active,
.drawer-leave-active {
  transition: transform 0.2s ease-out;
}
.drawer-enter-from,
.drawer-leave-to {
  transform: translateX(100%);
}

@media (prefers-reduced-motion: reduce) {
  .backdrop-enter-active,
  .backdrop-leave-active,
  .drawer-enter-active,
  .drawer-leave-active {
    transition: none;
  }
  .drawer-enter-from,
  .drawer-leave-to {
    transform: none;
  }
}

.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid var(--ds-neutral-200);
  flex-shrink: 0;
}

.drawer-logo {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-family: var(--ds-font-display);
  font-weight: 700;
  font-size: 1.15rem;
  color: var(--ds-primary-900);
  text-decoration: none;
}

.drawer-logo-icon {
  width: 28px;
  height: 28px;
  object-fit: contain;
}

.drawer-close {
  /* Reuse .hamburger as base; override to ensure visible on mobile. */
  display: inline-flex;
}

.drawer-nav {
  display: flex;
  flex-direction: column;
  padding: 8px 0;
}

.drawer-link {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 48px;
  padding: 0 16px;
  color: var(--ds-neutral-700);
  text-decoration: none;
  font-size: 16px;
  font-weight: 500;
  background: transparent;
  border: none;
  border-left: 3px solid transparent;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.drawer-link i {
  width: 18px;
  text-align: center;
  font-size: 0.95rem;
  color: var(--ds-neutral-500);
  transition: color 0.15s ease;
}

.drawer-link:hover,
.drawer-link:focus-visible {
  background: var(--ds-primary-100);
  color: var(--ds-primary-500);
  text-decoration: none;
  outline: none;
}

.drawer-link:hover i,
.drawer-link:focus-visible i {
  color: var(--ds-primary-500);
}

.drawer-link.router-link-active {
  color: var(--ds-primary-500);
  font-weight: 600;
  background: var(--ds-primary-100);
  border-left-color: var(--ds-primary-500);
}

.drawer-link.router-link-active i {
  color: var(--ds-primary-500);
}

.drawer-divider {
  margin: 4px 16px;
  border: none;
  border-top: 1px solid var(--ds-neutral-200);
}

.drawer-auth {
  display: flex;
  flex-direction: column;
  padding: 8px 0 16px;
}

.drawer-link--primary {
  margin: 8px 16px;
  background: var(--ds-primary-500);
  color: var(--ds-white);
  border-radius: var(--ds-radius-md);
  border-left: none;
  font-weight: 600;
  justify-content: flex-start;
  padding: 0 16px;
}

.drawer-link--primary i {
  color: var(--ds-white);
}

.drawer-link--primary:hover,
.drawer-link--primary:focus-visible {
  background: var(--ds-primary-400);
  color: var(--ds-white);
}

.drawer-link--primary:hover i,
.drawer-link--primary:focus-visible i {
  color: var(--ds-white);
}

.drawer-link--ghost {
  margin: 8px 16px 0;
  border: 1px solid var(--ds-neutral-200);
  border-radius: var(--ds-radius-md);
  border-left: 1px solid var(--ds-neutral-200);
  justify-content: flex-start;
}

.drawer-link--danger {
  color: var(--ds-danger, #dc2626);
}

.drawer-link--danger i {
  color: var(--ds-danger, #dc2626);
}

.drawer-link--danger:hover,
.drawer-link--danger:focus-visible {
  background: #fef2f2;
  color: var(--ds-danger, #dc2626);
}

/* Responsive Design */
@media (max-width: 1366px) {
  /* Eight primary links for a signed-in customer (Environments joined the
   * row on 2026-10-22) plus the persona switcher, Dashboard and the user
   * menu overran a 1280 px row — "Docs" and "Contact" slid under the
   * Dashboard button. Tighten the links and drop the persona label earlier
   * than the 1024 px step below; the row still stays single-line. */
  .nav-link {
    font-size: 0.88rem;
    padding-inline: 0.5rem;
  }

  .nav-links {
    gap: 2px;
  }

  .nav-persona :deep(.pw-trigger-label) { display: none; }
}

@media (max-width: 1024px) {
  .user-greeting {
    max-width: 140px;
    padding: 0.45rem 0.55rem;
  }

  .dashboard-btn {
    padding: 0.45rem 0.8rem;
    font-size: 0.85rem;
  }

  .logo {
    font-size: 1.25rem;
  }

  .logo-icon {
    width: 32px;
    height: 32px;
  }
}

@media (max-width: 820px) {
  /* Below 820px the desktop nav links would crowd into wrap territory.
   * Hide them and surface the hamburger → drawer instead. */
  .nav-links,
  .nav-persona {
    display: none;
  }

  .hamburger {
    display: inline-flex;
  }

  .logo {
    font-size: 1.3rem;
  }
}

@media (max-width: 480px) {
  .logo span {
    display: none;
  }

  .get-started-btn,
  .dashboard-btn {
    padding: 0.5rem 0.9rem;
    font-size: 0.85rem;
  }

  .btn-secondary {
    display: none;
  }

  .user-greeting {
    max-width: 120px;
  }
}

/* Org-switch full-screen loader */
.org-switch-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000000; /* above the navbar (999999) */
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.78); /* fallback */
  background: color-mix(in srgb, var(--ds-white) 78%, transparent);
  backdrop-filter: blur(3px);
}
.org-switch-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  background: var(--ds-white);
  border: 1px solid var(--ds-neutral-200);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-md);
  color: var(--ds-primary-900);
  font-family: var(--ds-font-display);
  font-weight: 600;
}
.org-switch-spin { color: var(--ds-primary-500); font-size: 1.25rem; }
.org-switch-fade-enter-active,
.org-switch-fade-leave-active { transition: opacity 0.18s ease; }
.org-switch-fade-enter-from,
.org-switch-fade-leave-to { opacity: 0; }
@media (prefers-reduced-motion: reduce) {
  .org-switch-spin { animation: none; }
  .org-switch-fade-enter-active,
  .org-switch-fade-leave-active { transition: none; }
}
</style>
